import React, { createContext, useState, useContext, useEffect } from 'react';
import { auth } from '../utils/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import apiClient from '../utils/apiClient';
import { syncCart, syncWishlist, getCart, getWishlist } from '../utils/firebaseSync';

const ShoppingContext = createContext();

export const ShoppingProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(true); // Track if auth state is being determined
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [syncInterval, setSyncInterval] = useState(null);

  // Initial load from localStorage (works even if backend is down or user not logged in)
  useEffect(() => {
    try {
      const localCart = JSON.parse(localStorage.getItem('cartItems') || '[]');
      const localWishlist = JSON.parse(localStorage.getItem('wishlistItems') || '[]');
      if (Array.isArray(localCart) && localCart.length > 0) setCartItems(localCart);
      if (Array.isArray(localWishlist) && localWishlist.length > 0) setWishlistItems(localWishlist);
    } catch (e) {
      // ignore parse errors
    } finally {
      setInitialLoadDone(true);
    }
  }, []);

  // Persist to localStorage on changes, but only after initial load
  useEffect(() => {
    if (initialLoadDone) {
      try { localStorage.setItem('cartItems', JSON.stringify(cartItems)); } catch { }
    }
  }, [cartItems, initialLoadDone]);

  useEffect(() => {
    if (initialLoadDone) {
      try { localStorage.setItem('wishlistItems', JSON.stringify(wishlistItems)); } catch { }
    }
  }, [wishlistItems, initialLoadDone]);

  // Load user cart when user logs in (overrides local with server copy if available)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        setAuthLoading(false);

        // Auto-create user in database if they don't exist
        try {
          const idToken = await firebaseUser.getIdToken();
          sessionStorage.setItem('token', idToken); // Store token for API calls

          await fetch(
            `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/verify-token`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${idToken}`,
                'Content-Type': 'application/json'
              }
            }
          );
        } catch (error) {
          console.error('Error verifying user:', error);
        }

        await loadUserCart(firebaseUser);
        await loadUserWishlist(firebaseUser);

        // Start periodic sync every 30 seconds when user is logged in
        const intervalId = setInterval(async () => {
          try {
            const freshUser = auth.currentUser;
            if (freshUser) {
              await loadUserCart(freshUser);
              await loadUserWishlist(freshUser);
            }
          } catch (error) {
            console.error('Periodic sync error:', error);
          }
        }, 30000);

        setSyncInterval(intervalId);
      } else {
        setUser(null);
        setAuthLoading(false);
        // Clear sync interval when logging out
        if (syncInterval) {
          clearInterval(syncInterval);
          setSyncInterval(null);
        }
        // Clear cart and wishlist on logout (don't keep logged-in user's items)
        setCartItems([]);
        setWishlistItems([]);
        localStorage.removeItem('cartItems');
        localStorage.removeItem('wishlistItems');
        sessionStorage.removeItem('token'); // Clear token
      }
    });

    return () => {
      unsubscribe();
      if (syncInterval) {
        clearInterval(syncInterval);
      }
    };
  }, [syncInterval]);

  // Load user's cart from Firebase (multi-device sync)
  const loadUserCart = async (firebaseUser) => {
    if (!firebaseUser) return;
    try {
      const items = await getCart(firebaseUser.uid);
      setCartItems(items);
    } catch (error) {
      console.error('Error loading cart:', error);
    }
  };

  // Load user's wishlist from Firebase (multi-device sync)
  const loadUserWishlist = async (firebaseUser) => {
    if (!firebaseUser) return;
    try {
      const items = await getWishlist(firebaseUser.uid);
      setWishlistItems(items);
    } catch (error) {
      console.error('Error loading wishlist:', error);
    }
  };

  // Save cart to Firebase (multi-device sync)
  const saveCartToBackend = async (items, currentUser = user) => {
    if (!currentUser) return;
    await syncCart(currentUser.uid, items);
  };

  // Manual refresh cart from backend
  const refreshCartFromBackend = async (currentUser = user) => {
    if (!currentUser) return;
    try {
      await loadUserCart(currentUser);
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  };

  // Manual refresh wishlist from backend
  const refreshWishlistFromBackend = async (currentUser = user) => {
    if (!currentUser) return;
    try {
      await loadUserWishlist(currentUser);
    } catch (error) {
      console.error('Error refreshing wishlist:', error);
    }
  };

  // Save wishlist to Firebase (multi-device sync)
  const saveWishlistToBackend = async (items, currentUser = user) => {
    if (!currentUser) return;
    await syncWishlist(currentUser.uid, items);
  };

  const addToCart = async (product) => {
    const newItems = cartItems.map(item => ({ ...item })); // Deep copy
    const existingItem = newItems.find((item) => item.id === product.id);

    if (existingItem) {
      existingItem.quantity += 1;
    } else {
      newItems.push({ ...product, quantity: 1 });
    }

    setCartItems(newItems);
    await saveCartToBackend(newItems);
  };

  const removeFromCart = async (productId) => {
    const newItems = cartItems.filter((item) => item.id !== productId);
    setCartItems(newItems);
    await saveCartToBackend(newItems);
  };

  const clearCart = async () => {
    setCartItems([]);
    await saveCartToBackend([]);
  };

  const updateCartQuantity = async (productId, quantity) => {
    if (quantity <= 0) {
      await removeFromCart(productId);
    } else {
      const newItems = cartItems.map((item) =>
        item.id === productId ? { ...item, quantity } : item
      );
      setCartItems(newItems);
      await saveCartToBackend(newItems);
    }
  };

  const addToWishlist = async (product) => {
    const newItems = wishlistItems.map(item => ({ ...item })); // Deep copy
    const exists = newItems.find((item) => item.id === product.id);

    if (exists) {
      // Remove from wishlist
      const filteredItems = newItems.filter((item) => item.id !== product.id);
      setWishlistItems(filteredItems);
      await saveWishlistToBackend(filteredItems);
    } else {
      // Add to wishlist
      newItems.push(product);
      setWishlistItems(newItems);
      await saveWishlistToBackend(newItems);
    }
  };

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <ShoppingContext.Provider
      value={{
        cartItems,
        wishlistItems,
        user,
        authLoading,
        addToCart,
        removeFromCart,
        clearCart,
        updateCartQuantity,
        addToWishlist,
        getTotalPrice,
        getCartCount,
        setUser,
        refreshCartFromBackend,
        refreshWishlistFromBackend,
      }}
    >
      {children}
    </ShoppingContext.Provider>
  );
};

export const useShopping = () => {
  const context = useContext(ShoppingContext);
  if (!context) {
    throw new Error('useShopping must be used within ShoppingProvider');
  }
  return context;
};
